import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useCustodyOrder } from "../../contexts/CustodyOrderContext";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "../../design/description-list";
import { Heading, Subheading } from "../../design/heading";
import { Divider } from "../../design/divider";
import DeleteCustodyOrderButton from "./components/DeleteCustodyOrderButton";
import LoadingView from "../../components/LoadingView";
import moment from "moment";
import EditCustodyOrderButton from "./components/EditCustodyOrderButton";
import ErrorView from "../../components/ErrorView";
import Breadcrumbs from "../../components/Breadcrumbs";
import { Navbar, NavbarItem } from "../../design/navbar";
import FileViewer from "../../components/FileViewer";
import { CustodyOrderService } from "../../services/CustodyOrderService";
import CustodyOrderTypeBadge from "../../components/CustodyOrderTypeBadge";

const CustodyOrderDetailPage: React.FC = () => {
  const { custodyOrderId } = useParams<{ custodyOrderId: string }>();
  const [state, setState] = React.useState<"Document" | "Tasks">("Document");
  const [fileUrl, setFileUrl] = React.useState<string | null>(null);
  const { selectedCustodyOrder, setSelectedCustodyOrder, error, loading } =
    useCustodyOrder();

  useEffect(() => {
    if (custodyOrderId) {
      setSelectedCustodyOrder(custodyOrderId);
    }
  }, [custodyOrderId]);

  useEffect(() => {
    if (!custodyOrderId) return;
    if (state !== "Document") return;
    const fetchFileUrl = async () => {
      const url = await CustodyOrderService.getDownloadURL(custodyOrderId!);
      setFileUrl(url);
    };
    fetchFileUrl();
  }, [custodyOrderId, state]);

  if (loading.details) {
    return <LoadingView title="Loading Custody Order" />;
  }

  if (error.details) {
    return <ErrorView error={error.details} />;
  }

  if (!selectedCustodyOrder) {
    return <ErrorView error={new Error("CustodyOrder not found")} />;
  }

  return (
    <div>
      <Breadcrumbs
        items={[
          { label: "Custody Orders", href: "/custody-orders" },
          {
            label: selectedCustodyOrder.title || selectedCustodyOrder?._id,
            href: `/custody-orders/${selectedCustodyOrder._id}`,
          },
        ]}
      />

      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="flex flex-col w-full gap-6">
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <Heading>{selectedCustodyOrder.title}</Heading>
              <div className="flex gap-2">
                <EditCustodyOrderButton custodyOrder={selectedCustodyOrder} />
                <DeleteCustodyOrderButton custodyOrderId={custodyOrderId!} />
              </div>
            </div>
          </div>
          <DescriptionList>
            <DescriptionTerm>Type</DescriptionTerm>
            <DescriptionDetails>
              <CustodyOrderTypeBadge type={selectedCustodyOrder.type} />
            </DescriptionDetails>
            <DescriptionTerm>Order Date</DescriptionTerm>
            <DescriptionDetails>
              {moment.utc(selectedCustodyOrder.orderDate).format("LL")}
            </DescriptionDetails>
            {selectedCustodyOrder.description && (
              <>
                <DescriptionTerm>Description</DescriptionTerm>
                <DescriptionDetails style={{ whiteSpace: "pre-line" }}>
                  {selectedCustodyOrder.description}
                </DescriptionDetails>
              </>
            )}
          </DescriptionList>
          {state === "Document" && fileUrl && <FileViewer fileUrl={fileUrl} />}
        </div>
      </div>
    </div>
  );
};

export default CustodyOrderDetailPage;
